import { CalendarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RetiringPerson } from '@shared/types';
import { api } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import { levelLabel } from '../pages/PersonCard.js';
import { ExportButtons } from './ExportButtons.js';

/**
 * รายชื่อผู้เกษียณ — ตอบคำถาม "ใครจะเกษียณเมื่อไหร่"
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องมี ทั้งที่ผังบอกจำนวนอยู่แล้ว
 * ────────────────────────────────────────────────────────────────
 *
 * ผังตอบได้ว่า "หน่วยนี้จะมีคนเกษียณ 9 คนใน 5 ปี" ซึ่งพอสำหรับมองภาพรวม
 * แต่งานที่ตามมาหลังจากนั้นต้องรู้ว่าใครบ้าง ตำแหน่งอะไร เกษียณวันไหน
 * แล้วเรียงตามวันเพื่อดูว่าต้องเริ่มสรรหาเรื่องไหนก่อน
 *
 * ผังจัดข้อมูลตามหน่วยงาน หน้านี้จัดตามเวลา จึงเป็นคนละมุมของข้อมูลชุดเดียวกัน
 * ไม่ใช่หน้าซ้ำ
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ หน้าที่อันตรายที่สุดในระบบ — อ่านก่อนแก้อะไรก็ตามในไฟล์นี้
 * ────────────────────────────────────────────────────────────────
 *
 * หน้านี้คือ "ชื่อคน + วันที่" ซึ่งเป็นรูปแบบเดียวกับเอกสารที่แนบเรื่องเสนอผู้บริหารได้ทันที
 * พอถูกพิมพ์หรือแคปหน้าจอออกไปแล้ว ไม่มีอะไรติดไปบอกว่าข้างในเป็นของสมมุติ
 *
 * แถบเตือนด้านบนจึงต้อง
 *   1. อยู่เหนือรายชื่อเสมอ ไม่ใช่ท้ายหน้า และยุบเก็บไม่ได้
 *   2. ตัดสินจาก roster.birthDateSource ที่เซิร์ฟเวอร์ส่งมา ห้ามเขียนเงื่อนไขตายไว้ที่นี่
 *
 * ข้อ 2 สำคัญกว่าที่เห็น — วันที่ DPIS ส่งวันเกิดจริงมา ค่าจะเปลี่ยนเป็น 'dpis' เอง
 * แถบเตือนจะหายไปเองโดยไม่ต้องแก้ไฟล์นี้ ถ้าเขียนเงื่อนไขตายไว้ จะกลายเป็นแถบที่ค้าง
 * อยู่ตลอดไปจนคนเลิกอ่าน แล้ววันที่มันควรเตือนจริง ๆ ก็จะไม่มีใครเห็นมันอีกแล้ว
 */
const YEAR_OPTIONS = [1, 3, 5, 10] as const;

export function RetirementRosterModal({ onClose }: { onClose: () => void }) {
  const [years, setYears] = useState<number>(5);
  const [unit, setUnit] = useState('');
  const [level, setLevel] = useState('');
  const [seniorOnly, setSeniorOnly] = useState(false);
  const [q, setQ] = useState('');
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  const roster = useQuery({
    queryKey: ['retirement-roster', years],
    queryFn: () => api.retirementRoster(years),
  });

  const needle = q.trim().toLowerCase();

  /**
   * กรองแล้วยังคงโครงกลุ่มรายปีไว้ และทิ้งปีที่ว่างเปล่า
   *
   * ⚠ ต้องนับ shown ที่นี่ ไม่ใช่ให้แต่ละกลุ่มนับเอง เพราะหัวข้อด้านบนต้องบอกได้ว่า
   * "แสดง 12 จาก 47 คน" — ตัวเลขที่กระทบยอดกับ KPI บนแถบไม่ได้คือจุดที่ผู้ใช้เลิกเชื่อทั้งหน้า
   */
  const { groups, shown } = useMemo(() => {
    const src = roster.data?.groups ?? [];
    let n = 0;
    const out = src
      .map((g) => {
        const people = g.people.filter((p) => {
          if (unit && p.unit !== unit) return false;
          if (level && p.level !== level) return false;
          if (seniorOnly && !p.senior) return false;
          if (needle && ![p.name, p.positionTitle, p.unit].join(' ').toLowerCase().includes(needle))
            return false;
          return true;
        });
        n += people.length;
        return { ...g, people };
      })
      .filter((g) => g.people.length > 0);
    return { groups: out, shown: n };
  }, [roster.data, unit, level, seniorOnly, needle]);

  const total = roster.data?.totals.people ?? 0;
  const filtered = shown !== total;

  /**
   * ตารางที่จะส่งออก — ส่งออก "สิ่งที่เห็นอยู่" ไม่ใช่ข้อมูลทั้งก้อน
   *
   * ผู้ใช้ตั้งตัวกรองไว้แล้วกดดาวน์โหลด เขาคาดหวังไฟล์ที่ตรงกับที่เห็นบนจอ
   * ถ้าได้ทุกคนทั้งกระทรวงกลับไป เขาต้องไปกรองใหม่ในไฟล์อีกรอบ
   * และที่แย่กว่าคือถ้าไม่ทันสังเกต จะเอาตัวเลขที่ไม่ตรงกับที่รายงานปากเปล่าไปใช้
   *
   * ⚠ หัวเรื่องย่อยจึงต้องบอกด้วยว่ากรองอะไรไว้ ไม่งั้นไฟล์ที่ส่งต่อไปแล้ว
   * จะไม่มีอะไรบอกว่าทำไมถึงมีแค่ 12 คน ทั้งที่ทั้งกระทรวงมี 624 คน
   */
  const buildExport = useCallback((): ExportTable => {
    const parts = [`ในอีก ${years} ปีข้างหน้า`];
    if (unit) parts.push(`หน่วยงาน ${unit}`);
    if (level) parts.push(`ระดับ ${level}`);
    if (seniorOnly) parts.push('เฉพาะระดับสูง');
    if (needle) parts.push(`ค้นหา "${q.trim()}"`);
    if (filtered) parts.push(`แสดง ${shown} จาก ${total} คน`);

    /* ⚠ เดิมเติมคำเตือนเรื่องวันเกิดสังเคราะห์เป็นบรรทัดแรกของไฟล์ที่ส่งออก
       ผู้ใช้สั่งเอาออกทั้งระบบ — birthDateSource ที่เซิร์ฟเวอร์ส่งมายังอยู่ครบ
       ถ้าจะเอากลับมา เติมกลับตรงนี้ที่เดียว */
    const warnings: string[] = [...(roster.data?.warnings ?? [])];

    return {
      title: 'รายชื่อผู้เกษียณอายุราชการ',
      subtitle: parts.join(' · '),
      warnings,
      columns: [
        { key: 'fy', label: 'อัตราว่าง 1 ต.ค.', width: 12, numeric: true },
        { key: 'date', label: 'วันเกษียณ', width: 14 },
        { key: 'name', label: 'ชื่อ-สกุล', width: 26 },
        { key: 'title', label: 'ตำแหน่ง', width: 30 },
        { key: 'level', label: 'ระดับ', width: 18 },
        { key: 'unit', label: 'หน่วยงาน', width: 34 },
        { key: 'age', label: 'อายุ', width: 8, numeric: true },
        { key: 'months', label: 'เหลืออีก (เดือน)', width: 14, numeric: true },
        { key: 'posNo', label: 'เลขที่ตำแหน่ง', width: 14 },
      ],
      rows: groups.flatMap((g) =>
        g.people.map((p) => ({
          fy: g.fiscalYear,
          date: p.retireDate,
          name: p.name,
          title: p.positionTitle,
          level: levelLabel(p.level),
          unit: p.unit,
          age: p.age,
          months: p.monthsUntil,
          posNo: p.posNo,
        })),
      ),
    };
  }, [groups, roster.data, years, unit, level, seniorOnly, needle, q, filtered, shown, total]);

  return (
    <div
      className="pmodal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pmodal is-wide" role="dialog" aria-modal="true" aria-label="บัญชีผู้เกษียณอายุราชการ">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><CalendarOutlined /></span>
          <div>
            <span className="kind">บัญชีผู้เกษียณอายุราชการ</span>
            <b>
              {roster.data
                ? filtered
                  ? `แสดง ${shown} จาก ${total} คน`
                  : `${total} คนใน ${years} ปีข้างหน้า`
                : 'กำลังโหลด…'}
            </b>
            <span className="sub">
              เรียงตามวันเกษียณอายุราชการ จำแนกตามวันที่อัตราว่าง (1 ต.ค.)
              {roster.data ? ` · ระดับสูง ${roster.data.totals.senior} คน` : ''}
            </span>
          </div>
          {roster.data && <ExportButtons build={buildExport} />}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        <div className="rosbody">
          <div className="rosbar">
            <div className="rosyears" role="group" aria-label="ช่วงเวลาที่มองไปข้างหน้า">
              {YEAR_OPTIONS.map((y) => (
                <button
                  key={y}
                  type="button"
                  aria-pressed={years === y}
                  onClick={() => setYears(y)}
                >
                  {y} ปี
                </button>
              ))}
            </div>

            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหาชื่อ ตำแหน่ง หรือหน่วยงาน"
              aria-label="ค้นหาในรายชื่อ"
            />

            <select value={unit} onChange={(e) => setUnit(e.target.value)} aria-label="กรองตามหน่วยงาน">
              <option value="">ทุกหน่วยงาน</option>
              {(roster.data?.filters.units ?? []).map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>

            <select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="กรองตามระดับ">
              <option value="">ทุกระดับ</option>
              {(roster.data?.filters.levels ?? []).map((l) => (
                <option key={l} value={l}>{levelLabel(l)}</option>
              ))}
            </select>

            <label className="roschk">
              <input
                type="checkbox"
                checked={seniorOnly}
                onChange={(e) => setSeniorOnly(e.target.checked)}
              />
              เฉพาะระดับสูง
            </label>
          </div>

          {roster.isPending && <p className="state">กำลังคำนวณวันเกษียณ…</p>}
          {roster.isError && <p className="state error">{String(roster.error)}</p>}

          {roster.data && groups.length === 0 && (
            <p className="state">ไม่มีใครเข้าเกณฑ์ที่กรองไว้</p>
          )}

          {groups.map((g) => (
            <section key={g.fiscalYear} className="rosgroup">
              <h4>
                เกษียณ 1 ต.ค. {g.fiscalYear}
                <span>
                  {g.people.length} คน
                  {g.people.filter((p) => p.senior).length > 0 &&
                    ` · ระดับสูง ${g.people.filter((p) => p.senior).length}`}
                </span>
              </h4>
              <ol className="roslist">
                {g.people.map((p, i) => (
                  <Row key={`${p.personaId ?? p.name}-${i}`} p={p} />
                ))}
              </ol>
            </section>
          ))}

          {roster.data && (
            <section className="pmodal-sec">
              <h4>ข้อจำกัดที่ต้องอ่านก่อนใช้</h4>
              <ul className="rotwarn">
                {roster.data.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ p }: { p: RetiringPerson }) {
  return (
    <li className={p.senior ? 'is-senior' : undefined}>
      <div className="ros-when">
        <b>{thaiDate(p.retireDate)}</b>
        <span>{untilLabel(p.monthsUntil)}</span>
      </div>
      <div className="ros-who">
        <div className="rc-hd">
          <b>{p.name}</b>
          {p.senior && <span className="rc-tag is-due">ระดับสูง</span>}
        </div>
        <span className="rc-sub">
          {p.positionTitle}
          {p.level && ` · ${levelLabel(p.level)}`}
          {p.posNo && ` · เลขที่ ${p.posNo}`}
        </span>
        <span className="rc-sub">{p.orgPath.length > 0 ? p.orgPath.join(' › ') : p.unit}</span>
      </div>
      <div className="ros-age">{p.age === null ? '—' : `${p.age} ปี`}</div>
    </li>
  );
}

/* ---------------- ตัวช่วย ---------------- */

const TH_MONTH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function thaiDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${TH_MONTH[Number(m) - 1]} ${Number(y) + 543}`;
}

/**
 * "อีกกี่เดือน" ต้องอ่านออกโดยไม่ต้องคิดเลขในหัว
 *
 * 0 เดือนไม่ได้แปลว่าเกษียณไปแล้ว แต่แปลว่าเกษียณภายในเดือนนี้ ซึ่งเป็นข้อมูลที่เร่งด่วนที่สุด
 * ในหน้านี้ จึงต้องเขียนให้ชัด ไม่ใช่ปล่อยเป็น "0 เดือน" ให้ตีความกันเอง
 */
function untilLabel(months: number | null): string {
  if (months === null) return 'ไม่ทราบ';
  if (months <= 0) return 'ภายในเดือนนี้';
  if (months < 12) return `อีก ${months} เดือน`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m ? `อีก ${y} ปี ${m} เดือน` : `อีก ${y} ปี`;
}
